import { Component, OnInit, ViewChild } from '@angular/core';
import { combineAll, DcBaseComponent } from '@datachain/ui-sdk/common';
import { Store } from '@ngrx/store';
import { DxDataGridComponent } from 'devextreme-angular';
import {
	BehaviorSubject,
	Observable,
	takeUntil,
	tap,
	combineLatest,
	firstValueFrom,
} from 'rxjs';

import { DcCoreElementType, DcCoreModule } from '../../../core/dc-core-types';
import { DcDxGrids, DxOperations } from '../../../core/dx-types';
import {
	DxListsStorage,
	IDxList,
} from '../../../core/services/dx-lists.storage';
import { ActionIcons } from '../../../ui/app-actions.icons';
import { ComponentIcons } from '../../../ui/app-components.icons';
import { DcIcons } from '../../../ui/app-dc.icons';
import { GdIcons } from '../../../ui/app-gd.icons';
import { HdIcons } from '../../../ui/app-hd.icons';
import { DcLoaders } from '../../../ui/app-loaders';
import { AppMetadataEntity } from '../../../ui/components/app-metadata/app-metadata.entity';
import {
	changeElementSelection,
	HomeSelector,
	initProjectHome,
	navigateToCreateElementRoute,
	navigateToElementListRoute,
	navigateToProjectEditRoute,
	refreshProjectTraceList,
} from '../../store';
import { ProjectTraceListEntity } from './project-trace-list.entity';

@Component({
	selector: 'app-project-home',
	templateUrl: './project-home.component.html',
	styleUrls: ['./project-home.component.scss'],
	inputs: ['module', 'projectId'],
})
export class ProjectHomeComponent extends DcBaseComponent implements OnInit {
	@ViewChild(DxDataGridComponent)
	private readonly grid: DxDataGridComponent | null = null;
	protected readonly noSorting = (): number => 0;
	protected readonly DcIcons = DcIcons;
	protected readonly GdIcons = GdIcons;
	protected readonly HdIcons = HdIcons;
	protected readonly DcLoaders = DcLoaders;
	protected readonly DxOperations = DxOperations;
	protected readonly ComponentIcons = ComponentIcons;
	protected readonly DcCoreElementType = DcCoreElementType;
	public vo$!: Observable<{
		isFirstLoad: boolean;
		module: DcCoreModule;
		metadata: AppMetadataEntity;
		stat: Map<DcCoreElementType, number>;
		selectedElement: DcCoreElementType;
		canCreate: boolean;
		canEdit: boolean;
		traces: ProjectTraceListEntity;
	}>;
	protected readonly ActionIcons = ActionIcons;
	private readonly firstLoadSubject = new BehaviorSubject<boolean>(true);
	public loadState = (): IDxList | undefined =>
		this.dxStorage.getListState(this.dxLocalStorageKey);

	public saveState = (state: IDxList): void => {
		this.dxStorage.setListState(this.dxLocalStorageKey, {
			...state,
			searchText: '',
		});
	};
	public descriptionMaxLength = 600;
	public descriptionMaxLines = 3;
	public module$ = this.toObservable<DcCoreModule>('module');
	public projectId$ = this.toObservable<string>('projectId');
	public constructor(
		private readonly store: Store,
		private readonly selector: HomeSelector,
		private readonly dxStorage: DxListsStorage
	) {
		super();
		this.cmpId = 'project-home';
		this.dxLocalStorageKey = DcDxGrids.ProjectTraceListStandalone;
	}

	public ngOnInit(): void {
		super.ngOnInit();
		combineLatest([this.projectId$, this.module$])
			.pipe(
				takeUntil(this.onDestroy$),
				tap(([projectId, module]) =>
					this.store.dispatch(
						initProjectHome({
							module,
							id: +projectId,
						})
					)
				)
			)
			.subscribe();

		this.vo$ = combineAll({
			isFirstLoad: this.firstLoadSubject,
			module: this.module$,
			metadata: this.selector.getProjectMetadata$(),
			stat: this.selector.getProjectStat$(),
			selectedElement: this.selector.getSelectedElement$(),
			canCreate: this.selector.getCanCreateElement$(),
			canEdit: this.selector.getCanEditProject$(),
			traces: this.selector.getTraces$(),
		});
	}

	public async navigateToEditRoute(): Promise<void> {
		const canEdit = await firstValueFrom(this.selector.getCanEditProject$());
		if (!canEdit) {
			return;
		}
		const projectId = await firstValueFrom(this.projectId$);
		this.store.dispatch(
			navigateToProjectEditRoute({
				id: +projectId,
			})
		);
	}

	public changeElementSelection(element: DcCoreElementType): void {
		this.store.dispatch(
			changeElementSelection({
				element,
			})
		);
	}

	public async navigateToElementList(
		element: DcCoreElementType
	): Promise<void> {
		const projectId = await firstValueFrom(this.projectId$);
		this.store.dispatch(
			navigateToElementListRoute({
				element,
				id: +projectId,
			})
		);
	}

	public async navigateToCreateElement(): Promise<void> {
		const projectId = await firstValueFrom(this.projectId$);
		this.store.dispatch(
			navigateToCreateElementRoute({
				id: +projectId,
			})
		);
	}

	public refresh(): void {
		this.store.dispatch(refreshProjectTraceList());
	}

	public search(searchTerm: string): void {
		if (this.grid === null) {
			return;
		}
		this.grid.instance.searchByText(searchTerm.trim());
	}
}
